import React, { useEffect, useRef } from 'react';
import { SyntaxHighlighter, oneDark } from '../../utils/syntaxHighlighter';
import { useToastStore } from '../../stores/ToastStore';

export const CodeBlock = React.memo(function CodeBlock({
	value,
	language,
	searchQuery,
	currentMatchIndex = -1,
	matchCounterRef,
}: {
	value: string;
	language?: string;
	searchQuery?: string;
	currentMatchIndex?: number;
	matchCounterRef?: React.MutableRefObject<number>;
}) {
	const { showToast } = useToastStore();
	const codeRef = useRef<HTMLDivElement>(null);

	const handleCopyCode = () => {
		navigator.clipboard
			.writeText(value)
			.then(() => {
				showToast('Code copied to clipboard', 'info', {
					timeout: 3000,
					placement: 'chat',
				});
			})
			.catch((err) => {
				console.error('Failed to copy code: ', err);
				showToast('Failed to copy code', 'error', {
					timeout: 3000,
					placement: 'chat',
				});
			});
	};

	const startingMatchIndex = matchCounterRef?.current ?? 0;
	let matchCount = 0;

	if (searchQuery && matchCounterRef) {
		const normalizedQuery = searchQuery.toLowerCase();
		const lowerValue = value.toLowerCase();
		let searchIndex = 0;

		while (searchIndex < lowerValue.length) {
			const index = lowerValue.indexOf(normalizedQuery, searchIndex);
			if (index === -1) break;
			matchCount++;
			searchIndex = index + 1;
		}

		// Increment the counter synchronously during render
		matchCounterRef.current += matchCount;
	}

	/**
	 * Apply search highlighting to code blocks after render
	 * Due to code block structure and formatting
	 */
	useEffect(() => {
		if (!searchQuery || !codeRef.current || matchCount === 0) return;

		const codeElement = codeRef.current;
		const pre = codeElement.querySelector('pre');
		if (!pre) return;

		// Get all text nodes within the code block
		const textNodes: Text[] = [];
		const walker = document.createTreeWalker(
			pre,
			NodeFilter.SHOW_TEXT,
			null,
		);

		let node: Node | null = walker.nextNode();
		while (node) {
			textNodes.push(node as Text);
			node = walker.nextNode();
		}

		let localMatchCounter = 0;
		const normalizedQuery = searchQuery.toLowerCase();
		textNodes.forEach((textNode) => {
			const text = textNode.textContent || '';
			const lowerText = text.toLowerCase();

			if (lowerText.includes(normalizedQuery)) {
				const parent = textNode.parentElement;
				if (!parent) return;

				const fragment = document.createDocumentFragment();
				let lastIndex = 0;
				let index = lowerText.indexOf(normalizedQuery);

				while (index !== -1) {
					// Add text before match
					if (index > lastIndex) {
						fragment.appendChild(
							document.createTextNode(
								text.substring(lastIndex, index),
							),
						);
					}

					// Add highlighted match
					const matchSpan = document.createElement('span');
					const globalMatchIndex =
						startingMatchIndex + localMatchCounter;
					const isCurrentMatch =
						globalMatchIndex === currentMatchIndex;
					matchSpan.className = isCurrentMatch
						? 'search-match search-match-current bg-amber-400/30 ring-1 ring-amber-400 rounded-sm px-0.5'
						: 'search-match bg-amber-400/20 rounded-sm px-0.5';
					matchSpan.textContent = text.substring(
						index,
						index + normalizedQuery.length,
					);
					fragment.appendChild(matchSpan);

					localMatchCounter++;
					lastIndex = index + normalizedQuery.length;
					index = lowerText.indexOf(normalizedQuery, lastIndex);
				}

				// Add remaining text
				if (lastIndex < text.length) {
					fragment.appendChild(
						document.createTextNode(text.substring(lastIndex)),
					);
				}

				parent.replaceChild(fragment, textNode);
			}
		});
	}, [searchQuery, value, currentMatchIndex, startingMatchIndex, matchCount]);

	return (
		<div className='formatted-code' ref={codeRef}>
			<div className='code-header'>
				<span>{language ?? 'output'}</span>
				<button
					onClick={handleCopyCode}
					className="bg-transparent border-0 cursor-pointer text-neutral-300 hover:text-neutral-100 transition-colors duration-200 p-1 rounded-sm"
					title="Copy code"
					aria-label="Copy code"
				>
					<i className="fas fa-copy text-xs"></i>
				</button>
			</div>
			{language ? (
				<SyntaxHighlighter style={oneDark} language={language}>
					{value}
				</SyntaxHighlighter>
			) : (
				<pre style={oneDark['pre[class*="language-"]']}>
					<code style={oneDark['code[class*="language-"]']}>
						{value}
					</code>
				</pre>
			)}
		</div>
	);
});
